import React from "react";
import {
  Box,
  ButtonBase,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import { useTreeViewContext } from "../TreeViewContext";
import { AddActionIcon, AddActionLabel } from "./AddAction";

const LEGEND_ITEMS = [
  { color: "primary.main", label: "Structure" },
  { color: "warning.main", label: "Opens elsewhere" },
  { color: "text.disabled", label: "Info only" },
];

/**
 * "Projects" heading, breadcrumb, list-wide "Add Project" button, search box
 * and color legend above the tree.
 */
export const TreeHeader = () => {
  const {
    treeData,
    collapsible,
    showLegend,
    pickerMode,
    listOpen,
    setListOpen,
    focusedPlant,
    setFocusedPlantId,
    handleSearchInput,
    plantOperations,
  } = useTreeViewContext();
  const { search, isSearchLoading } = treeData;
  const { openAddPlant } = plantOperations;

  const stepBackToFullList = () => {
    if (!focusedPlant) {
      setListOpen((open) => !open);
      return;
    }
    // Back to the full list means back to a collapsed one: what was left
    // open inside the plant being stepped out of (or opened by an earlier
    // search) would otherwise still be unfurled among all the other projects.
    setFocusedPlantId(null);
    treeData.setExpanded([]);
  };

  return (
    <>
      <Box sx={{ px: 2, py: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.25,
            minWidth: 0,
            mb: listOpen ? 1 : 0,
          }}
        >
          {collapsible ? (
            <ButtonBase
              // Doubles as a breadcrumb once a plant is picked: clicking
              // "Projects" then steps back out to the full list instead of
              // closing the panel's tree entirely.
              onClick={stepBackToFullList}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                borderRadius: 1,
                px: 0.5,
                ml: -0.5,
                py: 0.25,
                color: "text.secondary",
                "&:hover": { bgcolor: "action.hover", color: "text.primary" },
              }}
            >
              {listOpen ? (
                <ExpandMoreIcon fontSize="small" />
              ) : (
                <ChevronRightIcon fontSize="small" />
              )}
              <Typography variant="subtitle2" color="inherit">
                Projects
              </Typography>
            </ButtonBase>
          ) : (
            <Typography variant="subtitle2" color="text.secondary">
              Projects
            </Typography>
          )}
          {listOpen && focusedPlant && (
            <>
              <Typography variant="subtitle2" color="text.disabled">
                /
              </Typography>
              <Typography
                variant="subtitle2"
                noWrap
                title={focusedPlant.name}
                sx={{ minWidth: 0 }}
              >
                {focusedPlant.name}
              </Typography>
            </>
          )}
          {listOpen && !pickerMode && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flexShrink: 0,
                ml: "auto",
                pl: 1,
                pr: 0.75,
              }}
            >
              {/* The per-plant Document Register / Add WBS buttons live on
                  each project's own row; what the header carries is the
                  action for the list as a whole. */}
              <IconButton
                size="small"
                title="Add a project"
                onClick={openAddPlant}
                sx={{ p: 0.4, borderRadius: 1 }}
              >
                <AddActionIcon color="warning.main" />
                <AddActionLabel>Add Project</AddActionLabel>
              </IconButton>
            </Box>
          )}
        </Box>
        {listOpen && (
          <TextField
            value={search}
            onChange={(event) => handleSearchInput(event.target.value)}
            placeholder="Search project, WBS, doc..."
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="disabled" />
                </InputAdornment>
              ),
              endAdornment: isSearchLoading ? (
                <InputAdornment position="end">
                  <CircularProgress size={14} />
                </InputAdornment>
              ) : null,
            }}
          />
        )}
      </Box>
      <Divider />

      {showLegend && listOpen && (
        <>
          <Box
            sx={{
              px: 2,
              py: 0.75,
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            {LEGEND_ITEMS.map(({ color, label }) => (
              <Box
                key={label}
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                <Box
                  sx={{
                    width: 14,
                    height: 0,
                    borderTop: "2px solid",
                    borderColor: color,
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
          <Divider />
        </>
      )}
    </>
  );
};
